import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';

import { Button, Card, CardContent, Skeleton } from '@workspace/shadcn';

import { useInstructors } from '../../lib/api.ts';
import { usePageMeta } from '../../lib/seo.tsx';
import { Section } from '../components/section.tsx';
import { BackgroundLines } from '../components/effects/background-lines.tsx';

export default function Instructors() {
  const { data, isLoading, isError } = useInstructors();

  usePageMeta({
    title: 'ITGL Academy | Meet Our Instructors',
    description:
      'Embark on a learning journey guided by our expert instructors, each a beacon of wisdom in their field.',
    pathname: '/instructors',
  });

  const instructors = data?.records ?? [];

  return (
    <Section className="relative px-6 lg:px-10">
      <div className="mx-auto max-w-6xl py-8">
        <div className="relative mt-16 grid grid-cols-12 justify-center">
          <div className="relative col-span-12 h-fit text-center lg:col-span-8 lg:col-start-3">
            <div className="absolute -top-80 left-0 -z-10 h-screen w-full">
              <BackgroundLines />
            </div>
            <h1 className="mb-4 text-3xl leading-tight font-bold lg:text-5xl">
              Meet Our Knowledge Navigators
            </h1>
            <p className="text-muted-foreground mb-9 text-lg font-medium lg:px-12">
              Embark on a learning journey guided by our expert instructors,
              each a beacon of wisdom in their field.
            </p>
          </div>
        </div>

        {isError ? (
          <p className="text-muted-foreground mt-10 text-center">
            Our instructors are taking a moment to load. Please refresh the
            page.
          </p>
        ) : (
          <div className="mt-6 grid gap-6 sm:grid-cols-2 md:mt-14 lg:mt-20 lg:grid-cols-3">
            {isLoading
              ? Array.from({ length: 6 }, (_, index) => (
                  <Skeleton key={index} className="h-[360px] rounded-xl" />
                ))
              : instructors.map((instructor, index) => (
                  <Link
                    key={instructor.slug}
                    to={`/instructors/${instructor.slug}`}
                  >
                    <Card className="group h-full overflow-hidden transition-shadow duration-300 hover:shadow-lg">
                      <div className="overflow-hidden">
                        <img
                          loading={index < 3 ? 'eager' : 'lazy'}
                          src={instructor.imageUrl}
                          alt={instructor.name}
                          className="size-full object-cover object-[20%_20%] transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h2 className="mb-2 text-xl font-semibold">
                          {instructor.name}
                        </h2>
                        <p className="mb-2">{instructor.position}</p>
                        <p className="text-muted-foreground text-sm">
                          {instructor.institution}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
          </div>
        )}
      </div>

      <div className="mt-12 flex flex-col items-center justify-center">
        <div className="border-muted mb-6 h-px w-full border-t border-dashed" />
        <Link to="/courses">
          <Button className="group flex h-12 items-center gap-1 px-8 text-base">
            View All Courses
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </Link>
      </div>
    </Section>
  );
}
